import React, { useEffect, useState } from "react";
import { Modal, Input, Button, Form, notification } from "antd";
import { LuX, LuUpload } from "react-icons/lu";
import { useUploadFileMutation } from "../../store/api/apiSlice";
import { useDispatch, useSelector } from "react-redux";
import { setCredentials } from "../../store/auth/authSlice";
import axios from "axios";
import { getEnvironmentConfig } from "../../config/environment";

const { baseURL } = getEnvironmentConfig();

export const EditProfileModal = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [uploadFile] = useUploadFileMutation();

  const token = useSelector((state) => state.auth.token);
  const userData = useSelector((state) => state.auth.user);
  const userDatas = useSelector((state) => state.auth);

  const [isLoading, setIsLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    if (visible && userData) {
      const email = userData?.data?.email || userData?.email;
      const imageUrl = userData?.data?.image || userData?.image;

      form.setFieldsValue({
        first_name: userData?.data?.first_name || userData?.first_name,
        last_name: userData?.data?.last_name || userData?.last_name,
        email,
      });

      if (imageUrl) {
        setFileList([
          {
            uid: "-1",
            name: "profile-image",
            status: "done",
            url: imageUrl,
          },
        ]);
      } else {
        setFileList([]);
      }
    }
  }, [visible, userData, form]);

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);

      let imageUrl = userData?.data?.image || userData?.image || null;

      if (fileList[0]?.originFileObj) {
        try {
          const uploadResponse = await uploadFile({
            file: fileList[0].originFileObj,
            filename: "profile-image",
          }).unwrap();

          if (uploadResponse.file) {
            imageUrl = uploadResponse.file;
          }
        } catch (uploadError) {
          notification.error({
            message: "Upload Failed",
            description: "Failed to upload profile image",
          });
          return;
        }
      }

      const payload = {
        first_name: values.first_name,
        last_name: values.last_name,
        image: imageUrl,
        isAdmin: userDatas?.role === "admin",
      };

      const response = await axios.put(
        `${baseURL}/${userDatas?.role === "admin" ? "admin" : "user"}/profile`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data?.success) {
        notification.success({
          message: "Success",
          description: "Profile updated successfully",
        });

        const updatedUserData = {
          ...userData,
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          image: imageUrl,
          full_name: `${values.first_name} ${values.last_name}`.trim(),
          data: {
            ...userData?.data,
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
            image: imageUrl,
          },
        };

        localStorage.setItem("user", JSON.stringify(updatedUserData));

        dispatch(
          setCredentials({
            user: updatedUserData,
            token,
            role: userDatas.role,
            userData: {
              ...userDatas.userData,
              name: `${values.first_name} ${values.last_name}`,
              email: values.email,
              image: imageUrl,
            },
          }),
        );

        onClose();
      }
    } catch (error) {
      notification.error({
        message: "Update Failed",
        description:
          error.response?.data?.message || "Failed to update profile",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImagePreview = async (file) => {
    let src = file.url;

    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }

    const imgWindow = window.open("", "_blank");

    if (imgWindow) {
      const previewTitle = file?.name || "Image Preview";
      imgWindow.document.title = previewTitle;

      const previewImage = imgWindow.document.createElement("img");
      previewImage.src = src;
      previewImage.alt = previewTitle;
      previewImage.style.maxWidth = "80vw";
      previewImage.style.maxHeight = "80vh";
      previewImage.style.height = "auto";
      previewImage.style.display = "block";
      previewImage.style.borderRadius = "16px";

      imgWindow.document.body.style.margin = "0";
      imgWindow.document.body.style.backgroundColor = "#fff";
      imgWindow.document.body.style.display = "flex";
      imgWindow.document.body.style.justifyContent = "center";
      imgWindow.document.body.style.alignItems = "center";
      imgWindow.document.body.style.minHeight = "100vh";
      imgWindow.document.body.appendChild(previewImage);
    }
  };

  const handleProfileImageChange = (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) return;

    setFileList([
      {
        uid: String(Date.now()),
        name: selectedFile.name,
        status: "done",
        originFileObj: selectedFile,
        url: URL.createObjectURL(selectedFile),
      },
    ]);
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={500}
      closeIcon={
        <div className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full relative top-1">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      title={
        <h3 className="text-xl font-semibold relative -top-5 mb-0">
          Edit Profile
        </h3>
      }
    >
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <div className="space-y-6">
          <div className="w-full">
            <div className="w-full rounded-lg border border-dashed border-[#d9d9d9]">
              <label
                htmlFor="profile-image-input"
                className="flex cursor-pointer flex-col items-center justify-center gap-2 p-4 text-center"
              >
                {fileList[0]?.url ? (
                  <>
                    <img
                      src={fileList[0].url}
                      alt="Profile preview"
                      className="h-20 w-20 rounded-full object-cover"
                    />
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        handleImagePreview(fileList[0]);
                      }}
                      className="px-6 py-2 bg-[#202835] lg:w-[50%] sm:w-[100%] text-white flex items-center justify-center gap-2"
                      style={{ borderRadius: "16px" }}
                    >
                      Preview
                    </Button>
                  </>
                ) : (
                  <>
                    <LuUpload className="mx-auto" size={24} />
                    <span className="text-sm">Upload a Photo</span>
                  </>
                )}
              </label>
              <input
                id="profile-image-input"
                type="file"
                accept="image/*"
                className="sr-only"
                tabIndex={-1}
                onChange={handleProfileImageChange}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Form.Item
              name="first_name"
              label="First Name"
              className="mb-0"
              rules={[{ required: true, message: "First name is required" }]}
            >
              <Input
                placeholder="Enter first name"
                className="h-11 rounded-xl"
              />
            </Form.Item>
            <Form.Item
              name="last_name"
              label="Last Name"
              className="mb-0"
              rules={[{ required: true, message: "Last name is required" }]}
            >
              <Input
                placeholder="Enter last name"
                className="h-11 rounded-xl"
              />
            </Form.Item>
          </div>
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input
              disabled
              prefix={<span>✉</span>}
              placeholder="example@gmail.com"
              className="h-11 rounded-xl"
            />
          </Form.Item>
          <div className="flex flex-col sm:flex-row justify-between pt-4 gap-3 items-center">
            <Button
              onClick={onClose}
              className="px-6 py-2 border w-full sm:w-[100%] btnSecondary text-gray-700 flex items-center justify-center gap-2"
              style={{ borderRadius: "16px" }}
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              loading={isLoading}
              className="px-6 py-2 bg-[#202835] w-full sm:w-[100%] text-white flex items-center justify-center gap-2"
              style={{ borderRadius: "16px" }}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};
